/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Dialogs for Developer settings: create/edit an OAuth client, show a secret
 * once, and pick scopes.
 *
 * Scopes are shown as a grid of objects x Read / Create and edit / Delete,
 * which is how the role editor shows grants. A scope only says what a client
 * may attempt; the integration user's role still decides what it can do.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const ACTIONS = [
  { key: "read", label: "Read" },
  { key: "write", label: "Create and edit" },
  { key: "delete", label: "Delete" },
];

export const GRANT_LABELS = {
  client_credentials: "Server to server (client credentials)",
  authorization_code: "Acting for a signed-in user (authorization code + PKCE)",
  refresh_token: "Refresh tokens (stay connected)",
};

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      setCopied(false);
    }
  };
  return (
    <TextField
      fullWidth
      label={label}
      value={value || ""}
      margin="dense"
      InputProps={{
        readOnly: true,
        sx: { fontFamily: "monospace" },
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? "Copied" : "Copy"}>
              <IconButton onClick={copy} edge="end" aria-label={`Copy ${label}`}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}

export function SecretRevealDialog({ open, clientId, secret, onClose }) {
  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Save this client secret now</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This is the only time the secret is shown. PearlCRM stores a hash of it, not the
          secret itself. If you lose it, rotate the secret to get a new one.
        </Alert>
        <CopyField label="Client ID" value={clientId} />
        {secret && <CopyField label="Client secret" value={secret} />}
      </DialogContent>
      <DialogActions>
        <MDButton variant="gradient" color="info" onClick={onClose}>
          I have saved it
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

/** Object x action grid, plus the non-object scopes underneath. */
export function ScopePicker({ catalog, value, onChange }) {
  const selected = useMemo(() => new Set(value || []), [value]);
  const objects = useMemo(() => {
    const byObject = new Map();
    (catalog || []).forEach((s) => {
      if (!s.object) return;
      if (!byObject.has(s.object)) {
        byObject.set(s.object, { object: s.object, label: s.description.replace(/^Read /, ""), scopes: {} });
      }
      const action = s.scope.split(".").pop();
      byObject.get(s.object).scopes[action] = s.scope;
    });
    return Array.from(byObject.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog]);
  const special = useMemo(() => (catalog || []).filter((s) => !s.object), [catalog]);

  const toggle = (scope) => {
    const next = new Set(selected);
    if (next.has(scope)) next.delete(scope);
    else next.add(scope);
    onChange(Array.from(next));
  };
  const setColumn = (action, on) => {
    const next = new Set(selected);
    objects.forEach((o) => {
      const scope = o.scopes[action];
      if (!scope) return;
      if (on) next.add(scope);
      else next.delete(scope);
    });
    onChange(Array.from(next));
  };

  return (
    <MDBox>
      <MDBox sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell>Object</TableCell>
              {ACTIONS.map((a) => {
                const all = objects.length > 0 && objects.every((o) => selected.has(o.scopes[a.key]));
                return (
                  <TableCell key={a.key} align="center">
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={<Checkbox size="small" checked={all} onChange={(e) => setColumn(a.key, e.target.checked)} />}
                      label={<MDTypography variant="caption" fontWeight="medium">{a.label}</MDTypography>}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {objects.map((o) => (
              <TableRow key={o.object}>
                <TableCell sx={{ textTransform: "capitalize" }}>{o.label}</TableCell>
                {ACTIONS.map((a) => (
                  <TableCell key={a.key} align="center">
                    {o.scopes[a.key] && (
                      <Checkbox
                        size="small"
                        checked={selected.has(o.scopes[a.key])}
                        onChange={() => toggle(o.scopes[a.key])}
                        inputProps={{ "aria-label": `${a.label} ${o.label}` }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MDBox>
      <MDBox mt={1} display="flex" flexWrap="wrap" columnGap={2}>
        {special.map((s) => (
          <FormControlLabel
            key={s.scope}
            control={<Checkbox size="small" checked={selected.has(s.scope)} onChange={() => toggle(s.scope)} />}
            label={
              <MDTypography variant="caption">
                <code>{s.scope}</code> {s.description}
              </MDTypography>
            }
          />
        ))}
      </MDBox>
    </MDBox>
  );
}

const EMPTY = {
  name: "",
  description: "",
  client_type: "confidential",
  grant_types: ["client_credentials"],
  redirect_uris: "",
  allowed_scopes: [],
  integration_role_id: "",
  access_token_ttl_min: "",
  rate_limit_per_min: "",
};

/**
 * Create when `client` is null, edit otherwise. Client type cannot change
 * after creation: it decides whether the client has a secret at all.
 */
export function ClientEditorDialog({ open, client, catalog, roles, onClose, onSave, saving, error }) {
  const [form, setForm] = useState(EMPTY);
  const editing = Boolean(client);

  useEffect(() => {
    if (!open) return;
    if (client) {
      setForm({
        name: client.name || "",
        description: client.description || "",
        client_type: client.client_type,
        grant_types: client.grant_types || [],
        redirect_uris: (client.redirect_uris || []).join("\n"),
        allowed_scopes: client.allowed_scopes || [],
        integration_role_id: client.integration_role?.id || "",
        access_token_ttl_min: client.access_token_ttl_min || "",
        rate_limit_per_min: client.rate_limit_per_min || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, client]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  // A public client cannot use client_credentials, and its checkbox is
  // disabled, so drop the grant here or the default would stick and keep the
  // integration role required.
  const setClientType = (e) =>
    setForm((f) => ({
      ...f,
      client_type: e.target.value,
      grant_types: e.target.value === "public"
        ? f.grant_types.filter((g) => g !== "client_credentials")
        : f.grant_types,
    }));
  const toggleGrant = (grant) =>
    setForm((f) => {
      const has = f.grant_types.includes(grant);
      let next = has ? f.grant_types.filter((g) => g !== grant) : [...f.grant_types, grant];
      if (grant === "authorization_code" && has) next = next.filter((g) => g !== "refresh_token");
      return { ...f, grant_types: next };
    });

  const isPublic = form.client_type === "public";
  const wantsCC = form.grant_types.includes("client_credentials");
  const wantsCode = form.grant_types.includes("authorization_code");

  const submit = () => {
    const body = {
      name: form.name.trim(),
      description: form.description || null,
      grant_types: form.grant_types,
      redirect_uris: form.redirect_uris.split(/\s+/).map((s) => s.trim()).filter(Boolean),
      allowed_scopes: form.allowed_scopes,
      access_token_ttl_min: form.access_token_ttl_min ? Number(form.access_token_ttl_min) : null,
      rate_limit_per_min: form.rate_limit_per_min ? Number(form.rate_limit_per_min) : null,
    };
    if (!editing) {
      body.client_type = form.client_type;
      body.integration_role_id = wantsCC && form.integration_role_id ? Number(form.integration_role_id) : null;
    }
    onSave(body, editing && wantsCC && form.integration_role_id !== (client.integration_role?.id || "")
      ? Number(form.integration_role_id) : null);
  };

  const canSave = form.name.trim() && form.grant_types.length && form.allowed_scopes.length
    && (!wantsCC || form.integration_role_id) && (!wantsCode || form.redirect_uris.trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit ${client.name}` : "New OAuth client"}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth label="Name" value={form.name} onChange={set("name")} margin="dense" required
          helperText="Shown to people when this app asks for access, and in audit logs." />
        <TextField fullWidth label="Description" value={form.description} onChange={set("description")}
          margin="dense" multiline minRows={2} />

        <MDTypography variant="button" fontWeight="medium" display="block" mt={2}>Client type</MDTypography>
        <RadioGroup row value={form.client_type} onChange={editing ? undefined : setClientType}>
          <FormControlLabel value="confidential" disabled={editing} control={<Radio size="small" />}
            label="Confidential: runs on a server and can keep a secret" />
          <FormControlLabel value="public" disabled={editing} control={<Radio size="small" />}
            label="Public: a browser or mobile app, no secret (PKCE)" />
        </RadioGroup>

        <MDTypography variant="button" fontWeight="medium" display="block" mt={2}>How it gets tokens</MDTypography>
        {Object.entries(GRANT_LABELS).map(([grant, label]) => {
          const disabled = (grant === "client_credentials" && isPublic)
            || (grant === "refresh_token" && !wantsCode);
          return (
            <FormControlLabel key={grant} sx={{ display: "flex" }} disabled={disabled}
              control={<Checkbox size="small" checked={form.grant_types.includes(grant)} onChange={() => toggleGrant(grant)} />}
              label={<MDTypography variant="body2">{label}</MDTypography>} />
          );
        })}

        {wantsCC && (
          <MDBox mt={2}>
            <TextField select fullWidth label="Integration user role" value={form.integration_role_id}
              onChange={set("integration_role_id")} margin="dense" required
              helperText={"Server-to-server calls run as an integration user with this role. The role "
                + "applies even if your organisation does not enforce roles for people yet."}>
              {(roles || []).map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </TextField>
          </MDBox>
        )}

        {wantsCode && (
          <TextField fullWidth label="Redirect URIs" value={form.redirect_uris} onChange={set("redirect_uris")}
            margin="dense" multiline minRows={2} required
            helperText="One per line. Must match exactly. https only, except http://localhost for development." />
        )}

        <MDTypography variant="button" fontWeight="medium" display="block" mt={2} mb={1}>Scopes</MDTypography>
        <ScopePicker catalog={catalog} value={form.allowed_scopes}
          onChange={(scopes) => setForm((f) => ({ ...f, allowed_scopes: scopes }))} />

        <MDBox display="flex" gap={2} mt={2}>
          <TextField label="Token lifetime (minutes)" type="number" value={form.access_token_ttl_min}
            onChange={set("access_token_ttl_min")} margin="dense" inputProps={{ min: 5, max: 1440 }}
            helperText="Default 60" />
          <TextField label="Rate limit (requests/minute)" type="number" value={form.rate_limit_per_min}
            onChange={set("rate_limit_per_min")} margin="dense" inputProps={{ min: 1, max: 6000 }}
            helperText="Default 600" />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={saving}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={!canSave || saving}>
          {saving ? "Saving…" : editing ? "Save" : "Create client"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
